import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { grayMapStyle } from "@/utils/variables";
import { GoogleMap } from "@react-google-maps/api";
import { FC, PropsWithChildren, useEffect, useRef } from "react";
import { toast } from "sonner";

interface Props extends PropsWithChildren {
  libraries: any[];
  coordinates: {
    pickup_latitude: number;
    pickup_longitude: number;
    dropoff_latitude: number;
    dropoff_longitude: number;
  }
}

const Map: FC<Props> = ({ libraries, coordinates, children}) => {
  
  const {isLoaded, loadError} = useGoogleMaps();

  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(
    null,
  );

  const onMapLoad = (map: google.maps.Map) => {
    directionsRendererRef.current = new google.maps.DirectionsRenderer({
      map: map,
      polylineOptions: {
        strokeColor: "#0000FF",
        strokeOpacity: 0.8,
        strokeWeight: 5,
      },
    });
  };

  const calculateRoute = async () => {
    if (
      !coordinates.pickup_latitude ||
      !coordinates.pickup_longitude ||
      !coordinates.dropoff_latitude ||
      !coordinates.dropoff_latitude
    ) {
      console.warn("No coordinates to calculate route")
      return;
    }

    try {
      const directionsService = new window.google.maps.DirectionsService();
      const result = await directionsService.route({
        origin: {
          lat: coordinates.pickup_latitude,
          lng: coordinates.pickup_longitude,
        },
        destination: {
          lat: coordinates.dropoff_latitude,
          lng: coordinates.dropoff_longitude,
        },
        travelMode: google.maps.TravelMode.DRIVING,
      });

      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections(result);
      }
    } catch (error) {
      console.error("Error calculating route:", error);
      toast("Failed to calculate route. Please try again.", {
        className: "absolute bottom-10 right-10 z-50 text-red-500",
      });
    }
  };

  useEffect(() => {
    if (isLoaded) {
      calculateRoute();
    }
  }, [coordinates.pickup_latitude, coordinates.pickup_longitude, coordinates.dropoff_latitude, coordinates.dropoff_longitude, isLoaded]);

  if (loadError) return <p>Error loading Google Maps: {loadError.message}</p>;
  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <div className="!rounded-xl w-full min-h-[500px] h-full">
      {window.google && (
        <GoogleMap
          zoom={10}
          center={
            coordinates.pickup_latitude
              ? {
                  lat: coordinates.pickup_latitude,
                  lng: coordinates.pickup_longitude,
                }
              : { lat: 41.3275, lng: 19.8189 }
          }
          mapContainerClassName="w-full h-full rounded-xl pb-5"
          onLoad={onMapLoad}
          options={{
            styles: grayMapStyle,
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy"
          }}
        >
          {children}
        </GoogleMap>
      )}
    </div>
  );
};

export default Map
